// §18.7 chart spec: "ProgressRing: percent + absolute below." A hand-rolled SVG arc
// (stroke-dasharray technique) — no 3D, no gradient (18.7 explicitly forbids both for every
// chart), and no new charting dependency (this app has added exactly one, echarts, for
// WP-5.3's Sankey; a ring is simple enough to hand-roll). The dash-offset transition is
// capped at 150ms ease, 18.7's own "no animation beyond 150ms ease" ceiling.
export function ProgressRing({
  percent,
  size = 96,
  strokeWidth = 8,
  label,
}: {
  /** 0..100; out-of-range values are clamped here so callers never have to. */
  percent: number;
  size?: number;
  strokeWidth?: number;
  /** The "absolute below" half of the spec — e.g. allocated of target as an <Amount/> pair. */
  label?: React.ReactNode;
}): React.JSX.Element {
  const clamped = Math.max(0, Math.min(100, percent));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);

  return (
    <div className="flex flex-col items-center gap-1">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${String(size)} ${String(size)}`}
        role="img"
        aria-label={`${String(Math.round(clamped))}% complete`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--primary)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}
          style={{ transition: 'stroke-dashoffset 150ms ease' }}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--ink)"
          fontSize={size / 5.5}
          fontWeight={600}
        >
          {`${String(Math.round(clamped))}%`}
        </text>
      </svg>
      {label !== undefined && <div className="text-center text-xs text-muted">{label}</div>}
    </div>
  );
}
